/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';

import useSiteMetadata from '../../hooks/use_site_metadata';
import useTranslation from '../../hooks/use_translation';
import { createEvent } from '../../utils/tracking';

const Seo = ({
	description = '',
	image,
	location,
	meta = [],
	robots,
	schema,
	tags,
	title,
	tracking,
	type = 'website',
}) => {
	const { pathname } = location;
	const { language } = useTranslation('component_seo');
	const siteMetadata = useSiteMetadata();

	const createExternalUrl = (url, default_url = null) => {
		let out = url;
		if (!url) return out;

		if (url.indexOf('http') !== -1) {
			return out;
		} else if (!url && default_url) {
			out = default_url;
		} else out = `${siteMetadata.siteUrl}${url}`;

		return out;
	};

	const schema_url = 'https://schema.org';
	const logoUrl = `${siteMetadata.siteUrl}/assets/images/seo/logo.jpg`;
	const imageUrl = createExternalUrl(image, logoUrl);
	const metaDescription = description || siteMetadata.description;
	const pageUrl = `${siteMetadata.siteUrl}${pathname}`;
	let schemaData = [
		{
			'@context': schema_url,
			'@type': 'Organization',
			email: siteMetadata.supportEmail,
			logo: logoUrl,
			name: siteMetadata.title,
			sameAs: [
				'https://www.instagram.com/testmottagningen.se/',
				'https://www.youtube.com/channel/UCEeVTjKEVGK6ZMRjp8mgtKA',
			],
			telephone: siteMetadata.supportPhoneNumber,
			url: siteMetadata.siteUrl,
		},
	];

	let metaTags = [
		{
			content: metaDescription,
			name: 'description',
		},
		{
			content: tags,
			name: 'keywords',
		},
		{
			content: '494078745373596',
			property: 'fb:app_id',
		},
		{
			content: 'X1wK33hH6sTIrfHM0kUq7RIdoh5d4XpyxpaXHSyntcM',
			name: 'google-site-verification',
		},
		{
			content: schema?.[type]?.title || title,
			property: 'og:title',
		},
		{
			content: schema?.[type]?.description || metaDescription,
			property: 'og:description',
		},
		{
			content: createExternalUrl(schema?.[type]?.image, imageUrl),
			property: 'og:image',
		},
		{
			content: language,
			property: 'og:locale',
		},
		{
			content: logoUrl,
			property: 'og:logo',
		},
		{
			content: siteMetadata.title,
			property: 'og:site_name',
		},
		{
			content: type,
			property: 'og:type',
		},
		{
			content: pageUrl,
			property: 'og:url',
		},
		{
			content: 'summary',
			name: 'twitter:card',
		},
		{
			content: siteMetadata.author,
			name: 'twitter:creator',
		},
		{
			content: schema?.[type]?.description || metaDescription,
			name: 'twitter:description',
		},
		{
			content: createExternalUrl(schema?.[type]?.image, imageUrl),
			name: 'twitter:image',
		},
		{
			content: schema?.[type]?.title || title,
			name: 'twitter:title',
		},
	].concat(meta);

	if (schema?.article) {
		schemaData.push({
			'@context': schema_url,
			'@type': 'Article',
			datePublished: schema?.article?.published_at,
			headline: schema?.article?.title || title,
			image: [createExternalUrl(schema?.article?.image)],
			url: schema?.article?.url || '',
		});
	}

	if (schema?.faq?.items?.length) {
		schemaData.push({
			'@context': schema_url,
			'@type': 'FAQPage',
			mainEntity: schema?.faq?.items?.map((item) => ({
				'@type': 'Question',
				acceptedAnswer: {
					'@type': 'Answer',
					text: item.answer,
				},
				name: item.question,
			})),
		});
	}

	if (schema?.location) {
		schemaData.push({
			'@context': schema_url,
			'@type': 'MedicalBusiness',
			address: {
				'@type': 'PostalAddress',
				addressCountry: 'SE',
				addressLocality: schema?.location?.city,
				postalCode: schema?.location?.zip_code,
				streetAddress: schema?.location?.address,
			},
			image: [createExternalUrl(schema?.location?.image)],
			name: schema?.location?.title || title,
			paymentAccepted: 'Credit Card',
			telephone: siteMetadata.supportPhoneNumber,
			url: schema?.location?.url || '',
		});
	}

	if (schema?.product) {
		schemaData.push({
			'@context': schema_url,
			'@type': 'Product',
			brand: {
				'@type': 'Brand',
				name: 'Testmottagningen',
			},
			description: schema?.product?.description || description,
			image: [createExternalUrl(schema?.product?.image)],
			name: schema?.product?.title || title,
			offers: [
				{
					'@type': 'Offer',
					availability: `${schema_url}/InStock`,
					itemCondition: `${schema_url}/NewCondition`,
					price: schema?.product?.price || 0,
					priceCurrency: schema?.product?.currency || 'SEK',
					priceValidUntil: '2026-06-20',
					sku: `${schema?.id && schema?.variant?.id ? schema?.product?.id + '-' + schema?.product?.variant?.id : undefined}`,
					url: pageUrl,
				},
			],
			productID: schema?.product?.id,
			sku: schema?.product?.id,
			url: pageUrl,
		});

		metaTags = [
			...metaTags,
			{
				content: 'in stock',
				property: 'product:availability',
			},
			{
				content: 'Testmottagningen',
				property: 'product:brand',
			},
			{
				content: 'new',
				property: 'product:condition',
			},
			{
				content: schema.price || 0,
				property: 'product:price:amount',
			},
			{
				content: schema.currency || 'SEK',
				property: 'product:price:currency',
			},
			{
				content: schema.id,
				property: 'product:retailer_item_id',
			},
		];
	}

	if (robots) {
		metaTags = [
			...metaTags,
			{
				content: robots,
				name: 'robots',
			},
		];
	}

	useEffect(() => {
		if (tracking?.content_group) {
			createEvent('content_group', {
				id: tracking.content_group,
			});
		}
	}, [tracking?.content_group]);

	return (
		<Helmet
			htmlAttributes={{ lang: language }}
			titleTemplate={`%s | ${siteMetadata.title}`}
		>
			<title>{title}</title>
			<link href={pageUrl} rel="canonical" />

			{metaTags.map((item, index) => {
				if (item.property) {
					return (
						<meta content={item.content} key={index} property={item.property} />
					);
				}
				return <meta content={item.content} key={index} name={item.name} />;
			})}

			{schemaData?.map((item, index) => (
				<script key={index} type="application/ld+json">
					{JSON.stringify(item)}
				</script>
			))}
			<script
				async
				src="//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
				type="text/javascript"
			></script>
		</Helmet>
	);
};

Seo.propTypes = {
	description: PropTypes.string,
	image: PropTypes.string,
	location: PropTypes.object.isRequired,
	meta: PropTypes.array,
	robots: PropTypes.string,
	schema: PropTypes.object,
	tags: PropTypes.string,
	title: PropTypes.string.isRequired,
	tracking: PropTypes.object,
	type: PropTypes.string,
};

export default Seo;
